import { t, type TFunction } from "i18next";

import { DateRangeQuickFilters } from "@/components/ui/v2/date-range-picker";
import type { ReportingLevel } from "@/features/integrations/modules/reporting/api/types";
import type { ModuleUiColumnDefinition, UiComponent } from "@/features/integrations/types";

export type TabLevel = Exclude<ReportingLevel, "candidates">;

export * from "@/features/integrations/modules/reporting/reportingFilterConfig";

// ---------------------------------------------------------------------------
// Table columns
// ---------------------------------------------------------------------------

/** Metric columns are identical across every level — shared scope/metric fields from IReportingData.
 * Hiding one via the column selector removes it from every level's table. */
export const METRIC_COLUMNS: ModuleUiColumnDefinition[] = [
  { key: "cost", label: "reporting.cost", type: "Currency", textAlign: "right", showNullAs: "N/A", showZeroAs: "N/A" },
  { key: "impressions", label: "reporting.impressions", type: "Int", textAlign: "right", showNullAs: "N/A", showZeroAs: "N/A" },
  { key: "ctr", label: "reporting.ctr", type: "Percentage", textAlign: "right", showNullAs: "N/A", showZeroAs: "N/A" },
  { key: "clicks", label: "reporting.clicks", type: "Int", textAlign: "right", showNullAs: "N/A", showZeroAs: "N/A" },
  { key: "cpc", label: "reporting.cpc", type: "Currency", textAlign: "right", showNullAs: "N/A", showZeroAs: "N/A" },
  { key: "startApply", label: "reporting.startApply", type: "Int", textAlign: "right", showNullAs: "N/A", showZeroAs: "N/A" },
  { key: "cpsa", label: "reporting.cpsa", type: "Currency", textAlign: "right", showNullAs: "N/A", showZeroAs: "N/A" },
  { key: "applications", label: "reporting.applications", type: "Int", textAlign: "right", showNullAs: "N/A", showZeroAs: "N/A" },
  { key: "cpa", label: "reporting.cpa", type: "Currency", textAlign: "right", showNullAs: "N/A", showZeroAs: "N/A" },
];

/** Vacancy-level dimension columns (non-metric). These populate the "Vacancy" group of the
 * column selector and only render on the vacancies level. */
export const VACANCY_DIMENSION_COLUMNS: ModuleUiColumnDefinition[] = [
  // vacancyId (external vacancy reference = publication id) renders as a deep link using the row's
  // `jobSourceUrl` (built server-side); plain text when absent.
  { key: "vacancyId", label: "reporting.vacancyId", type: "Link", hrefKey: "jobSourceUrl", showNullAs: "N/A" },
  { key: "vacancyTitle", label: "reporting.titleColumn", type: "String", showNullAs: "N/A" },
  { key: "department", label: "reporting.department", type: "String", showNullAs: "N/A" },
  { key: "company", label: "reporting.company", type: "String", showNullAs: "N/A" },
  { key: "country", label: "reporting.country", type: "String", showNullAs: "N/A" },
  { key: "city", label: "reporting.city", type: "String", showNullAs: "N/A" },
  { key: "field", label: "reporting.field", type: "String", showNullAs: "N/A" },
];

export const REPORTING_COLUMNS_VACANCY: ModuleUiColumnDefinition[] = [
  ...VACANCY_DIMENSION_COLUMNS,
  ...METRIC_COLUMNS,
];

export const REPORTING_COLUMNS_CAMPAIGN: ModuleUiColumnDefinition[] = [
  { key: "campaignName", label: "reporting.campaignName", type: "String", showNullAs: "N/A" },
  ...METRIC_COLUMNS,
];

export const REPORTING_COLUMNS_INTEGRATION: ModuleUiColumnDefinition[] = [
  { key: "integrationName", label: "reporting.integrationName", type: "String", showNullAs: "N/A" },
  ...METRIC_COLUMNS,
];

export const COLUMNS_BY_LEVEL: Record<TabLevel, ModuleUiColumnDefinition[]> = {
  vacancies: REPORTING_COLUMNS_VACANCY,
  campaigns: REPORTING_COLUMNS_CAMPAIGN,
  integrations: REPORTING_COLUMNS_INTEGRATION,
};

/** localStorage key persisting the hidden-column keys for the General Reporting table. */
export const COLUMN_VISIBILITY_STORAGE_KEY = "ReportingHiddenColumns";

/**
 * Translated groups for the column selector: a "Vacancy" group (vacancy-specific dimensions, only
 * rendered on the vacancies level) and a "Metrics" group (shared by every level, so hiding a metric
 * removes it from all tabs). Uses i18next's standalone `t`, so call it during render (e.g. directly
 * in the component body) — the consuming component re-renders on language change and re-translates.
 */
export function getColumnSelectorGroups() {
  return [
    {
      id: "vacancy",
      label: t("reporting.columnGroups.vacancy", "Vacancy"),
      options: VACANCY_DIMENSION_COLUMNS.map((c) => ({ key: c.key, label: t(c.label) })),
    },
    {
      id: "metrics",
      label: t("reporting.columnGroups.metrics", "Metrics"),
      options: METRIC_COLUMNS.map((c) => ({ key: c.key, label: t(c.label) })),
    },
  ];
}

/**
 * Sort-key allowlist per reporting level — derived from each level's column set so the frontend
 * can drop an incompatible `orderBy` when switching tabs (e.g., sorting by `vacancyTitle` on the
 * Vacancy tab then jumping to Campaign). Without this filter the next tab issues a request the
 * backend's per-level sort allowlist rejects with 400 and the table renders empty.
 *
 * Metric keys (cost, ctr, cpc, …) are shared across every level, so a metric-based sort survives
 * cross-tab navigation. Level-specific keys (vacancyTitle, campaignName, integrationName) only
 * survive into their own tab.
 */
export const SORTABLE_KEYS_BY_LEVEL: Record<TabLevel, ReadonlySet<string>> = {
  vacancies: new Set(REPORTING_COLUMNS_VACANCY.map((c) => c.key)),
  campaigns: new Set(REPORTING_COLUMNS_CAMPAIGN.map((c) => c.key)),
  integrations: new Set(REPORTING_COLUMNS_INTEGRATION.map((c) => c.key)),
};

/** Row identity column per level — used by the data table for React keys and row references. */
const ROW_ID_BY_LEVEL: Record<TabLevel, string> = {
  vacancies: "vacancyId",
  // CampaignName is always non-null (Unattributed bucket has the literal "Unattributed") and is
  // unique within a single page's dataset; using it as the row id avoids null collisions that
  // would happen with `campaignId` (which is null for Unattributed).
  campaigns: "campaignName",
  integrations: "integrationName",
};

// ---------------------------------------------------------------------------
// Table / toolbar config
// ---------------------------------------------------------------------------

export const TABLE_TOOLBAR_CONFIG = {
  searchPlaceholderKey: "reporting.searchVacancyTitle" as const,
  dateRangeFilterKey: "dateRange" as const,
  rowIdColumn: "vacancyId" as const,
};

export const DATE_RANGE_PICKER_CONFIG = {
  allowedPresetIds: DateRangeQuickFilters.allExceptToday,
};

// ---------------------------------------------------------------------------
// Metrics cards (title key, value key, subtitle key, and data field name)
// ---------------------------------------------------------------------------

export const METRICS_CONFIG = [
  {
    titleKey: "reporting.totalCost" as const,
    valueKey: "totalCost" as const,
    subtitleKey: "reporting.metricsVacancies" as const,
    countKey: "totalRecords" as const,
    format: "currency" as const,
  },
  {
    titleKey: "reporting.averageCtr" as const,
    valueKey: "averageCtr" as const,
    subtitleKey: "reporting.metricsImpressions" as const,
    countKey: "totalImpressions" as const,
    format: "percentage" as const,
  },
  {
    titleKey: "reporting.averageCpc" as const,
    valueKey: "averageCpc" as const,
    subtitleKey: "reporting.metricsClicks" as const,
    countKey: "totalClicks" as const,
    format: "currency" as const,
  },
  {
    titleKey: "reporting.averageCpsa" as const,
    valueKey: "averageCpsa" as const,
    subtitleKey: "reporting.metricsStartApplies" as const,
    countKey: "totalStartApply" as const,
    format: "currency" as const,
  },
  {
    titleKey: "reporting.averageCpa" as const,
    valueKey: "averageCpa" as const,
    subtitleKey: "reporting.metricsApplications" as const,
    countKey: "totalApplications" as const,
    format: "currency" as const,
  },
] as const;

/**
 * The total-cost metric card's subtitle counts rows in the table, so its label varies per level
 * ("X Vacancies" / "X Campaigns" / "X Integrations"). Other metric cards count metric sums
 * (impressions, clicks, …) which are level-invariant and use the static subtitle key from METRICS_CONFIG.
 */
export const TOTAL_COST_SUBTITLE_KEY_BY_LEVEL: Record<TabLevel, string> = {
  vacancies: "reporting.metricsVacancies",
  campaigns: "reporting.metricsCampaigns",
  integrations: "reporting.metricsIntegrations",
};

/** Max characters to show for vacancy title in the table; rest is truncated with ellipsis and full title shown on hover. */
export const TITLE_COLUMN_MAX_LENGTH = 40;

/** Columns that use custom cell renderers and their format. Page builds renderers from this. */
export const CUSTOM_CELL_RENDERERS_CONFIG = [
  { columnKey: "cpc", format: "currency" as const },
  { columnKey: "cpsa", format: "currency" as const },
  { columnKey: "cpa", format: "currency" as const },
  { columnKey: "ctr", format: "percentage" as const },
] as const;

// ---------------------------------------------------------------------------
// Page / integration labels
// ---------------------------------------------------------------------------

export const PAGE_CONFIG = {
  titleKey: "reporting.title" as const,
  subtitleKey: "reporting.generalDescription" as const,
  emptyMessageKey: "reporting.noData" as const,
  vacanciesFoundEntityKey: "reporting.vacanciesFoundEntity" as const,
  integrationName: "general" as const,
  moduleName: "reporting" as const,
};

// ---------------------------------------------------------------------------
// UI component tree (table toolbar + data table)
// ---------------------------------------------------------------------------

/**
 * Builds the General Reporting page UI component array for a given level.
 * `columns` is the (already visibility-filtered) column set to render; the row id column is
 * derived from the level.
 */
export function getGeneralReportingUiComponents(
  t: TFunction,
  level: TabLevel,
  columns: ModuleUiColumnDefinition[],
): UiComponent[] {
  return [
    {
      component: "tableToolbar",
      props: {
        search: {
          placeholder: t(TABLE_TOOLBAR_CONFIG.searchPlaceholderKey, "Search vacancy title"),
          enabled: true,
        },
        filters: [
          {
            key: TABLE_TOOLBAR_CONFIG.dateRangeFilterKey,
            type: "DateRange",
            inlineWithSearch: true,
          },
        ],
      },
    },
    {
      component: "dataTable",
      props: {
        rowIdColumn: ROW_ID_BY_LEVEL[level],
        columns,
      },
    },
  ];
}
